import type { Vec3 } from '../../utils/math';
import type { Pose } from '../connectors/Pose';

/**
 * Centre-line geometry of track modules (pure math, no engine types).
 *
 * Gameplay always works in path coordinates: x = lateral offset from the centre
 * line, y = height, z = s = meters along the centre line. Straight modules map
 * them 1:1 to segment-local space. Curve modules (SEG_15/16) bend the centre
 * line into a circular arc that starts at CONNECTOR_IN heading +Z; every lane
 * keeps its lateral offset, so lanes, obstacles, collectibles, decorations and
 * ad slots follow the arc. Presentation places visuals with `pathToLocal`.
 */

/** Point on a segment's centre line in segment-local space; yaw = local heading (radians). */
export interface PathPoint {
  x: number;
  z: number;
  yaw: number;
}

/** Segment-local position + heading of something given in path coordinates. */
export interface PathPlacement {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/** Below this curvature (1/m) an arc is treated as straight. */
const STRAIGHT_CURVATURE = 1e-9;

/** Curvature (1/m, + = right turn) of an arc turning `angle` radians over `length` meters. */
export function arcCurvature(length: number, angle: number): number {
  return angle / length;
}

/** Radius of the centre line (meters). Infinity for a straight. */
export function arcRadius(length: number, angle: number): number {
  return Math.abs(angle) < STRAIGHT_CURVATURE ? Infinity : length / Math.abs(angle);
}

/**
 * Centre-line point of an arc with the given curvature after s meters, starting at the
 * origin heading +Z (yaw 0 → +Z, +π/2 → +X, matching Pose/Babylon rotation.y).
 */
export function arcPoint(curvature: number, s: number, out: PathPoint = { x: 0, z: 0, yaw: 0 }): PathPoint {
  const yaw = curvature * s;
  if (Math.abs(curvature) < STRAIGHT_CURVATURE) {
    out.x = 0;
    out.z = s;
    out.yaw = 0;
    return out;
  }
  const half = Math.sin(yaw / 2);
  // (1 − cos θ) / κ written as 2 sin²(θ/2) / κ – no cancellation near the start.
  out.x = (2 * half * half) / curvature;
  out.z = Math.sin(yaw) / curvature;
  out.yaw = yaw;
  return out;
}

const scratch: PathPoint = { x: 0, z: 0, yaw: 0 };

/** Path coordinates (lateral, height, s) on an arc → segment-local position + heading. */
export function arcPlacement(curvature: number, lateral: number, y: number, s: number, out: PathPlacement = { x: 0, y: 0, z: 0, yaw: 0 }): PathPlacement {
  const p = arcPoint(curvature, s, scratch);
  // right vector of the heading: (cos yaw, −sin yaw) – see Pose.rightOf
  out.x = p.x + Math.cos(p.yaw) * lateral;
  out.y = y;
  out.z = p.z - Math.sin(p.yaw) * lateral;
  out.yaw = p.yaw;
  return out;
}

/** Exit transform of an arc module (end of the centre line, heading = turn angle). */
export function arcExitPose(length: number, angle: number): Pose {
  const end = arcPoint(arcCurvature(length, angle), length);
  return { x: end.x, y: 0, z: end.z, yaw: angle };
}

/** The part of a definition the path depends on. */
export interface PathShape {
  readonly length: number;
  /** Curve modules only: total heading change over the length (radians, + = right). */
  readonly curve?: { readonly angle: number };
}

/** Curvature of a module's centre line (0 for straights and junctions). */
export function curvatureOf(shape: PathShape): number {
  return shape.curve ? arcCurvature(shape.length, shape.curve.angle) : 0;
}

/**
 * Path coordinates (socket data: x lateral, y height, z along the path) → segment-local
 * placement for visuals. Identity for straight modules and junction approaches.
 */
export function pathToLocal(shape: PathShape, path: Readonly<Vec3>, out: PathPlacement = { x: 0, y: 0, z: 0, yaw: 0 }): PathPlacement {
  const curvature = curvatureOf(shape);
  if (curvature === 0) {
    out.x = path.x;
    out.y = path.y;
    out.z = path.z;
    out.yaw = 0;
    return out;
  }
  return arcPlacement(curvature, path.x, path.y, path.z, out);
}

/**
 * Lateral offset at which a straight chord of `chord` meters, centred on a line at
 * `lateral`, keeps its ends `lateral` away from the centre line: on the outside of a
 * curve a flat panel (ad banner) would otherwise poke behind the fence. Returns the
 * inward shift (meters, ≥ 0) for the outer side, 0 for the inner side and straights.
 */
export function outerChordInset(shape: PathShape, lateral: number, chord: number): number {
  const curvature = curvatureOf(shape);
  if (curvature === 0 || Math.sign(lateral) === Math.sign(curvature)) return 0; // inner side or straight
  const radius = 1 / Math.abs(curvature) + Math.abs(lateral);
  return radius - Math.sqrt(radius * radius - (chord / 2) ** 2);
}
